<template>
  <div class="petmed-filters">
    <v-autocomplete
      :value="value.clinicIds"
      :items="clinics"
      :loading="loadingClinics"
      :disabled="clinicsError"
      :hint="clinicsError ? 'No se pudieron cargar las clínicas; se muestran todas las clínicas Droopy.' : ''"
      :persistent-hint="clinicsError"
      item-text="clinicName"
      item-value="clinicId"
      label="Clínicas"
      placeholder="Todas las clínicas Droopy"
      persistent-placeholder
      multiple
      clearable
      dense
      outlined
      :hide-details="!clinicsError"
      class="clinic-field"
      @change="onClinics"
    >
      <template v-slot:item="{ item, attrs }">
        <v-list-item-action class="mr-3">
          <v-simple-checkbox :value="attrs.inputValue" :ripple="false" />
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title class="d-flex align-center">
            <v-icon small class="mr-2" :color="isActive(item) ? 'success' : 'grey'">
              {{ isActive(item) ? 'mdi-check-circle' : 'mdi-close-circle' }}
            </v-icon>
            <span>{{ item.clinicName }}</span>
            <span v-if="!isActive(item)" class="pw-pct ml-2 inactive-label">Inactiva</span>
          </v-list-item-title>
        </v-list-item-content>
      </template>
      <template v-slot:selection="{ item }">
        <v-chip small close class="clinic-chip" @click:close="removeClinic(item.clinicId)">
          <v-icon x-small left :color="isActive(item) ? 'success' : 'grey'">
            {{ isActive(item) ? 'mdi-check-circle' : 'mdi-close-circle' }}
          </v-icon>
          {{ item.clinicName }}
        </v-chip>
      </template>
    </v-autocomplete>

    <div class="d-flex align-center flex-wrap date-row">
      <v-menu v-model="fromMenu" :close-on-content-click="false" offset-y min-width="auto">
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="value.fromDate || 'Histórico'"
            label="Desde"
            prepend-inner-icon="mdi-calendar"
            readonly
            dense
            outlined
            hide-details
            class="date-field"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          :value="value.fromDate"
          :max="value.toDate || today"
          locale="es-cl"
          first-day-of-week="1"
          no-title
          @input="onFromDate"
        />
      </v-menu>

      <v-menu v-model="toMenu" :close-on-content-click="false" offset-y min-width="auto">
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="value.toDate"
            label="Hasta"
            prepend-inner-icon="mdi-calendar"
            readonly
            dense
            outlined
            hide-details
            class="date-field"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          :value="value.toDate"
          :min="value.fromDate"
          :max="today"
          locale="es-cl"
          first-day-of-week="1"
          no-title
          @input="onToDate"
        />
      </v-menu>

      <v-chip-group :value="value.preset" active-class="preset-chip--active" class="preset-group" @change="onPreset">
        <v-chip
          v-for="p in presets"
          :key="p.value"
          :value="p.value"
          small
          outlined
        >
          {{ p.label }}
        </v-chip>
      </v-chip-group>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { presetRange } from '@/utils/petmed-metrics.utils';

const PRESETS = [
  { value: 'currentMonth', label: 'Mes actual' },
  { value: 'previousMonth', label: 'Mes anterior' },
  { value: 'last3Months', label: 'Últimos 3 meses' },
  { value: 'historical', label: 'Histórico' },
];

export default {
  name: 'PetmedFiltersBar',

  props: {
    // { fromDate: 'YYYY-MM-DD' | null, toDate: 'YYYY-MM-DD', clinicIds: string[], preset: string | null }
    value: { type: Object, required: true },
    clinics: { type: Array, default: () => [] },
    loadingClinics: { type: Boolean, default: false },
    clinicsError: { type: Boolean, default: false },
  },

  data() {
    return {
      fromMenu: false,
      toMenu: false,
      presets: PRESETS,
      today: moment().format('YYYY-MM-DD'),
    };
  },

  methods: {
    emit(patch) {
      this.$emit('input', { ...this.value, ...patch });
    },
    onFromDate(date) {
      this.fromMenu = false;
      this.emit({ fromDate: date, preset: null });
    },
    onToDate(date) {
      this.toMenu = false;
      this.emit({ toDate: date, preset: null });
    },
    // v-chip-group emite undefined al deseleccionar el chip activo; se ignora
    // para que siempre quede un rango aplicado.
    onPreset(preset) {
      if (!preset) return;
      this.emit({ ...presetRange(preset), preset });
    },
    onClinics(clinicIds) {
      this.emit({ clinicIds: clinicIds || [] });
    },
    removeClinic(clinicId) {
      this.emit({ clinicIds: this.value.clinicIds.filter((id) => id !== clinicId) });
    },
    // Mismo criterio que DroopyClinics.vue: /droopy-clinics devuelve status 'active' | 'inactive'.
    isActive(clinic) {
      return clinic.status === 'active';
    },
  },
};
</script>

<style scoped>
.date-row {
  gap: 12px;
  margin-top: 12px;
}
.date-field {
  max-width: 170px;
}
.clinic-field {
  min-width: 260px;
  max-width: 460px;
}
.inactive-label {
  color: var(--pw-text-faint);
}
.clinic-chip {
  margin: 2px;
}
.preset-group >>> .v-chip {
  border-color: var(--pw-border-strong);
  color: var(--pw-text-muted);
}
.preset-group >>> .preset-chip--active {
  border-color: var(--pw-primary);
  color: var(--pw-primary);
  background: var(--pw-primary-tint) !important;
}
</style>
